/**
 * Autocomplete Input Component
 * Smart input with suggestions from purchase history
 */

import { useState, useEffect, useRef } from 'react';
import { itemsAPI } from '@/services/api';
import Input from './Input';

const AutocompleteInput = ({ 
  value, 
  onChange, 
  onSelect, 
  placeholder = 'Start typing...',
  disabled = false,
  className = ''
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [loading, setLoading] = useState(false);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  /**
   * Fetch suggestions from history based on input
   */
  useEffect(() => {
    const fetchSuggestions = async () => {
      if (!value || value.trim().length < 2) {
        setSuggestions([]);
        setShowSuggestions(false);
        return;
      }

      setLoading(true);
      try {
        const response = await itemsAPI.getHistory(value.trim(), 10);
        
        if (response.success && response.data?.suggestions) {
          setSuggestions(response.data.suggestions);
          setShowSuggestions(response.data.suggestions.length > 0);
        }
      } catch (error) {
        console.error('Error fetching suggestions:', error);
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    };

    // Debounce the API call
    const timeoutId = setTimeout(fetchSuggestions, 300);
    return () => clearTimeout(timeoutId);
  }, [value]);

  /**
   * Handle click outside to close suggestions
   */
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  /**
   * Handle keyboard navigation
   */
  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      
      case 'Enter':
        if (selectedIndex >= 0) {
          e.preventDefault();
          handleSelectSuggestion(suggestions[selectedIndex]);
        }
        break;
      
      case 'Escape':
        setShowSuggestions(false);
        setSelectedIndex(-1);
        break;
      
      default:
        break;
    }
  };

  /**
   * Handle suggestion selection
   */
  const handleSelectSuggestion = (suggestion) => {
    if (onSelect) {
      onSelect(suggestion);
    } else {
      onChange({ target: { value: suggestion.name } });
    }
    setShowSuggestions(false);
    setSelectedIndex(-1);
    inputRef.current?.focus();
  };

  /**
   * Handle input change
   */
  const handleInputChange = (e) => {
    onChange(e);
    setSelectedIndex(-1);
  };

  /**
   * Handle input focus
   */
  const handleFocus = () => {
    if (suggestions.length > 0 && value.trim().length >= 2) {
      setShowSuggestions(true);
    }
  };

  return (
    <div ref={wrapperRef} className={`relative ${className}`}>
      <Input
        ref={inputRef}
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        placeholder={placeholder}
        disabled={disabled}
        className="w-full"
      />
      
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700 max-h-60 overflow-auto">
          {loading && (
            <div className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400">
              Loading suggestions...
            </div>
          )}
          
          {!loading && (
            <ul className="py-1">
              {suggestions.map((suggestion, index) => (
                <li
                  key={`${suggestion.name}-${index}`}
                  onClick={() => handleSelectSuggestion(suggestion)}
                  className={`px-4 py-2 cursor-pointer transition-colors ${
                    index === selectedIndex
                      ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                      : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium text-sm text-gray-900 dark:text-gray-100">{suggestion.name}</div>
                      {suggestion.notes && (
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                          {suggestion.notes}
                        </div>
                      )}
                    </div>
                    {suggestion.quantity && suggestion.quantity > 1 && (
                      <span className="text-xs text-gray-400 dark:text-gray-500 ml-2">
                        x{suggestion.quantity}
                      </span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
          
          <div className="border-t border-gray-100 dark:border-gray-700 px-4 py-2 text-xs text-gray-400 dark:text-gray-500">
            {suggestions.length} suggestion{suggestions.length !== 1 ? 's' : ''} from your history
          </div>
        </div>
      )}
    </div>
  );
};

export default AutocompleteInput;
